import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { generateXRayKneeReport } from "../../utils/xrayknee"; // Import from the separate file
import { API_BASE_URL } from "../Api/apiconnector";

export default function XRayKneeForm({ onClose, currentUser, generateReport, onBack, onSubmit }) {
  const [formData, setFormData] = useState({
    name: "",
    patientId: "",
    age: "",
    testDate: "",
    reportDate: "",
    gender: "Male",
    xrayView: "",
    normalXray: false,
    leftNormal: false,
    rightNormal: false,
    left: {
      finding: false,
      degenerative: false,
      deformity: false,
      genu: "",
      fracture: false,
      LinearUndisplacedFracture: false,
      LineardisplacedFracture: false,
      ComminutedUndisplacedFracture: false,
      ComminuteddisplacedFracture: false,
      ComminutedDepressedFracture: false,
      degenerativeFindings: {
        medialJoint: false,
        lateralJoint: false,
        subchondralSclerosis: false,
        subchondralCysts: false,
        tibialSpiking: false,
        marginalOsteophytes: false,
        looseBodies: false,
      },
      fractures: {
        tibia: false,
        fibula: false,
        lowerFemur: false,
        patella: false,
        orthoImplants: false,
        orthoImplantsTibia: false,
        orthoImplantsFemur: false,
        orthoImplantsFibula: false,
        orthoImplantsPatella: false,
        tibiaFractureTypes: {
        LinearUndisplacedFracture: false,
        LineardisplacedFracture: false,
        ComminutedUndisplacedFracture: false,
        ComminuteddisplacedFracture: false,
        ComminutedDepressedFracture: false,
        },
        fibulaFractureTypes: {
        LinearUndisplacedFracture: false,
        LineardisplacedFracture: false,
        ComminutedUndisplacedFracture: false,
        ComminuteddisplacedFracture: false,
        ComminutedDepressedFracture: false,
        },
        lowerFemurFractureTypes: {
        LinearUndisplacedFracture: false,
        LineardisplacedFracture: false,
        ComminutedUndisplacedFracture: false,
        ComminuteddisplacedFracture: false,
        ComminutedDepressedFracture: false,
        },
      },
    },
    right: {
      finding: false,
      degenerative: false,
      deformity: false,
      genu: "",
      fracture: false,
      LinearUndisplacedFracture: false,
      LineardisplacedFracture: false,
      ComminutedUndisplacedFracture: false,
      ComminuteddisplacedFracture: false,
      ComminutedDepressedFracture: false,
      degenerativeFindings: {
        medialJoint: false,
        lateralJoint: false,
        subchondralSclerosis: false,
        subchondralCysts: false,
        tibialSpiking: false,
        marginalOsteophytes: false,
        looseBodies: false,
      },
      fractures: {
        tibia: false,
        fibula: false,
        lowerFemur: false,
        patella: false,
        orthoImplants: false,
        tibiaFractureTypes: {
        LinearUndisplacedFracture: false,
        LineardisplacedFracture: false,
        ComminutedUndisplacedFracture: false,
        ComminuteddisplacedFracture: false,
        ComminutedDepressedFracture: false,
    },
    fibulaFractureTypes: {
        LinearUndisplacedFracture: false,
        LineardisplacedFracture: false,
        ComminutedUndisplacedFracture: false,
        ComminuteddisplacedFracture: false,
        ComminutedDepressedFracture: false,
    },
    lowerFemurFractureTypes: {
        LinearUndisplacedFracture: false,
        LineardisplacedFracture: false,
        ComminutedUndisplacedFracture: false,
        ComminuteddisplacedFracture: false,
        ComminutedDepressedFracture: false,
    },
    },
      },
    });

  const [expandedSections, setExpandedSections] = useState({
    left: false,
    right: false,
  });

  useEffect(() => {
    const fetchDemographics = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const patientId = urlParams.get("id");
        if (!patientId) return;

        const response = await fetch(`${API_BASE_URL}/fetch-dicom/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ id: patientId }),
        });

        if (!response.ok)
          throw new Error(`Failed to fetch demographics (${response.status})`);
        const data = await response.json();
        console.log("✅ Fetched demographics:", data);

        const formattedStudyDate = data.study_date || "";
        const now = new Date();
        const formattedReportDate = formatToDDMMYYYY(now);
        const formattedReportTime = now.toLocaleTimeString("en-GB");

        setFormData((prev) => ({
          ...prev,
          name: data.patient_name || "",
          patientId: data.patient_id || "",
          age: data.age || "",
          gender: data.gender || "Male",
          referralDoctor: data.referring_doctor_name || "",
          testDate: formattedStudyDate,
          reportDate: formattedReportDate,
          reportTime: formattedReportTime,
        }));
      } catch (error) {
        console.error("❌ Error fetching DICOM demographics:", error);
      }
    };

    fetchDemographics();
  }, []);

  // Helper functions
  function formatToDDMMYYYY(date) {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  // Function to map form data to the expected format for generateXRayKneeReport
  const mapFormDataToReportFormat = () => {
  const mappedData = {
    // Patient Information
    NameTextFR15: formData.name,
    IDTextFR15: formData.patientId,
    AgeTextFR15: formData.age,
    GenderTextFR15: formData.gender,
    ReferralDrTextFR15: formData.referralDoctor || "",
    TestDateTextFR15: formData.testDate,
    ReportDateTextFR15: formData.reportDate,
    ReporttimeTextFR15: formData.reportTime,

    // X-Ray Type and Normal
    XrayType: formData.xrayView,
    KneeNormal: formData.normalXray,
    KneeNormalL: formData.leftNormal,
    KneeNormalR: formData.rightNormal,
    LeftKnee: formData.left.finding,
    RightKnee: formData.right.finding,

    // Left Side Data
    LeftDegenerative: formData.left.degenerative,
    LeftDegenerativeMedialJoint: formData.left.degenerativeFindings.medialJoint,
    LeftDegenerativelateralJointSpace: formData.left.degenerativeFindings.lateralJoint,
    LeftDegenerativeSubchondralSclerosis: formData.left.degenerativeFindings.subchondralSclerosis,
    LeftDegenerativeSubchondralSysts: formData.left.degenerativeFindings.subchondralCysts,
    LeftDegenerativeTibialSpiking: formData.left.degenerativeFindings.tibialSpiking,
    LeftDegenerativeMarginalOsteophytes: formData.left.degenerativeFindings.marginalOsteophytes,
    LeftDegenerativeLooseBodies: formData.left.degenerativeFindings.looseBodies,
    LeftDegenerativeDeformity: formData.left.deformity,
    LeftDegenerativeDeformityTypes: formData.left.genu,

    LeftFracture: formData.left.fracture,
    LeftTibia: formData.left.fractures.tibia,
    LeftFibula: formData.left.fractures.fibula,
    LeftLowerThirdofFemur: formData.left.fractures.lowerFemur,
    LeftPatella: formData.left.fractures.patella,
    LeftOrthopaedicImplants: formData.left.fractures.orthoImplants,
    
    // Left Orthopedic Implant Details - USE ACTUAL FORM DATA
    LeftOrthopaedicTibia: formData.left.fractures.orthoImplantsTibia,
    LeftOrthopaedicFemur: formData.left.fractures.orthoImplantsFemur,
    LeftOrthopaedicFibula: formData.left.fractures.orthoImplantsFibula,
    LeftOrthopaedicPlateau: formData.left.fractures.orthoImplantsPatella,

    // Left side fractures
    LeftLinearUndisplacedFracture: formData.left.fractures.tibiaFractureTypes.LinearUndisplacedFracture,
    LeftLineardisplacedFracture: formData.left.fractures.tibiaFractureTypes.LineardisplacedFracture,
    LeftComminutedUndisplacedFracture: formData.left.fractures.tibiaFractureTypes.ComminutedUndisplacedFracture,
    LeftComminuteddisplacedFracture: formData.left.fractures.tibiaFractureTypes.ComminuteddisplacedFracture,
    LeftComminutedDepressedFracture: formData.left.fractures.tibiaFractureTypes.ComminutedDepressedFracture,

    LeftLinearUndisplacedFracture1: formData.left.fractures.fibulaFractureTypes.LinearUndisplacedFracture,
    LeftLineardisplacedFracture1: formData.left.fractures.fibulaFractureTypes.LineardisplacedFracture,
    LeftComminutedUndisplacedFracture1: formData.left.fractures.fibulaFractureTypes.ComminutedUndisplacedFracture,
    LeftComminuteddisplacedFracture1: formData.left.fractures.fibulaFractureTypes.ComminuteddisplacedFracture,
    LeftComminutedDepressedFracture1: formData.left.fractures.fibulaFractureTypes.ComminutedDepressedFracture,

    LeftLinearUndisplacedFracture2: formData.left.fractures.lowerFemurFractureTypes.LinearUndisplacedFracture,
    LeftLineardisplacedFracture2: formData.left.fractures.lowerFemurFractureTypes.LineardisplacedFracture,
    LeftComminutedUndisplacedFracture2: formData.left.fractures.lowerFemurFractureTypes.ComminutedUndisplacedFracture,
    LeftComminuteddisplacedFracture2: formData.left.fractures.lowerFemurFractureTypes.ComminuteddisplacedFracture,
    LeftComminutedDepressedFracture2: formData.left.fractures.lowerFemurFractureTypes.ComminutedDepressedFracture,

    // Right Side Data
    RightDegenerative: formData.right.degenerative,
    RightDegenerativeMedialJoint: formData.right.degenerativeFindings.medialJoint,
    RightDegenerativelateralJointSpace: formData.right.degenerativeFindings.lateralJoint,
    RightDegenerativeSubchondralSclerosis: formData.right.degenerativeFindings.subchondralSclerosis,
    RightDegenerativeSubchondralSysts: formData.right.degenerativeFindings.subchondralCysts,
    RightDegenerativeTibialSpiking: formData.right.degenerativeFindings.tibialSpiking,
    RightDegenerativeMarginalOsteophytes: formData.right.degenerativeFindings.marginalOsteophytes,
    RightDegenerativeLooseBodies: formData.right.degenerativeFindings.looseBodies,
    RightDegenerativeDeformity: formData.right.deformity,
    RightDegenerativeDeformityTypes: formData.right.genu,

    RightFracture: formData.right.fracture,
    RightTibia: formData.right.fractures.tibia,
    RightFibula: formData.right.fractures.fibula,
    RightLowerThirdofFemur: formData.right.fractures.lowerFemur,
    RightPatella: formData.right.fractures.patella,
    RightOrthopaedicImplants: formData.right.fractures.orthoImplants,

    // Right Orthopedic Implant Details - USE ACTUAL FORM DATA
    RightOrthopaedicTibia: formData.right.fractures.orthoImplantsTibia,
    RightOrthopaedicFemur: formData.right.fractures.orthoImplantsFemur,
    RightOrthopaedicFibula: formData.right.fractures.orthoImplantsFibula,
    RightOrthopaedicPlateau: formData.right.fractures.orthoImplantsPatella,

    // --- Tibia Fracture Types ---
    RightLinearUndisplacedFracture: formData.right.fractures.tibiaFractureTypes.LinearUndisplacedFracture,
    RightLineardisplacedFracture: formData.right.fractures.tibiaFractureTypes.LineardisplacedFracture,
    RightComminutedUndisplacedFracture: formData.right.fractures.tibiaFractureTypes.ComminutedUndisplacedFracture,
    RightComminuteddisplacedFracture: formData.right.fractures.tibiaFractureTypes.ComminuteddisplacedFracture,
    RightComminutedDepressedFracture: formData.right.fractures.tibiaFractureTypes.ComminutedDepressedFracture,

    // --- Fibula Fracture Types ---
    RightLinearUndisplacedFracture1: formData.right.fractures.fibulaFractureTypes.LinearUndisplacedFracture,
    RightLineardisplacedFracture1: formData.right.fractures.fibulaFractureTypes.LineardisplacedFracture,
    RightComminutedUndisplacedFracture1: formData.right.fractures.fibulaFractureTypes.ComminutedUndisplacedFracture,
    RightComminuteddisplacedFracture1: formData.right.fractures.fibulaFractureTypes.ComminuteddisplacedFracture,
    RightComminutedDepressedFracture1: formData.right.fractures.fibulaFractureTypes.ComminutedDepressedFracture,

    // --- Femur Fracture Types ---
    RightLinearUndisplacedFracture2: formData.right.fractures.lowerFemurFractureTypes.LinearUndisplacedFracture,
    RightLineardisplacedFracture2: formData.right.fractures.lowerFemurFractureTypes.LineardisplacedFracture,
    RightComminutedUndisplacedFracture2: formData.right.fractures.lowerFemurFractureTypes.ComminutedUndisplacedFracture,
    RightComminuteddisplacedFracture2: formData.right.fractures.lowerFemurFractureTypes.ComminuteddisplacedFracture,
    RightComminutedDepressedFracture2: formData.right.fractures.lowerFemurFractureTypes.ComminutedDepressedFracture,

    // Default values for required fields
    LeftDegenerativeReducedJointSpaces: false,
    RightDegenerativeReducedJointSpaces: false,
    Fracture: formData.left.fracture || formData.right.fracture,
    reportimage: null,
  };

  return mappedData;
};
  // Function to handle form submission and generate report
  const handleSubmit = () => {
    // Map form data to the expected format
    const reportData = mapFormDataToReportFormat();
    
    // Generate the report
    const generatedReport = generateXRayKneeReport(reportData, currentUser);
    
    // Pass the report to the parent component (to be displayed in editor)
    if (generateReport) {
        generateReport(generatedReport);
    }
    
    console.log("✅ Report generated and sent to editor", generatedReport);
    if (onClose) onClose();
    // Don't call onClose immediately - let the parent component handle closing
    // after the editor has been updated
    // onClose(); // Remove this line or let parent handle closing
  };

  // Alternative: If you want to keep the form open to review the generated report
  const handleSubmitAndPreview = () => {
    const reportData = mapFormDataToReportFormat();
    const generatedReport = generateXRayKneeReport(reportData, currentUser);
    
    if (onSubmit) {
      onSubmit(generatedReport);
    }
    
    console.log("✅ Report generated and preview sent to editor", generatedReport);
    
    // You can show a success message instead of immediately closing
    alert("Report generated successfully! Check the editor for the generated content.");
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (side, field, subfield) => {
    setFormData((prev) => {
      const updatedSide = { ...prev[side] };
      if (subfield) {
        updatedSide[field] = {
          ...updatedSide[field],
          [subfield]: !updatedSide[field][subfield],
        };
      } else {
        updatedSide[field] = !updatedSide[field];
      }
      return { ...prev, [side]: updatedSide };
    });
  };

  // Helper for checkbox consistent red styling
  const checkboxClass =
    "w-5 h-5 rounded accent-red-600 focus:ring-red-500 cursor-pointer";

  // Helper to render nested structure for one side (Left or Right)
  const renderKneeSection = (side) => {
    const data = formData[side];
    const capitalizedSide = side.charAt(0).toUpperCase() + side.slice(1);

    return (
      <div className="p-6">
        {/* Normal side toggle */}
        {formData.normalXray && (
          <label className="flex items-center gap-3 cursor-pointer select-none mb-4">
            <input
              type="checkbox"
              checked={formData[`${side}Normal`]}
              onChange={() =>
                handleInputChange(`${side}Normal`, !formData[`${side}Normal`])
              }
              className={checkboxClass}
            />
            <span className="text-gray-800 font-semibold">
              {capitalizedSide} Normal?
            </span>
          </label>
        )}

        {/* Expand Button */}
        <button
          onClick={() => toggleSection(side)}
          className="w-full flex items-center justify-between p-3 border border-gray-300 rounded-lg hover:bg-gray-100 transition mb-4"
        >
          <span className="font-semibold text-gray-800">
            {capitalizedSide} Knee Details
          </span>
          {expandedSections[side] ? (
            <ChevronUp className="text-gray-600" />
          ) : (
            <ChevronDown className="text-gray-600" />
          )}
        </button>

        {expandedSections[side] && (
          <div className="space-y-4">
            {/* Finding */}
            <label className="flex items-center gap-3 cursor-pointer select-none pl-4">
              <input
                type="checkbox"
                checked={data.finding}
                onChange={() => handleCheckboxChange(side, "finding")}
                className={checkboxClass}
              />
              <span className="text-gray-800">
                Finding {capitalizedSide} Knee?
              </span>
            </label>

            {data.finding && (
              <div className="pl-6 space-y-3">
                {/* Degenerative */}
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={data.degenerative}
                    onChange={() => handleCheckboxChange(side, "degenerative")}
                    className={checkboxClass}
                  />
                  <span className="text-gray-800">Degenerative?</span>
                </label>

                {/* Degenerative Sub-options */}
                {data.degenerative && (
                  <div className="pl-6 space-y-2">
                    {[
                      { key: "medialJoint", label: "Medial Joint?" },
                      { key: "lateralJoint", label: "Lateral Joint Space?" },
                      { key: "subchondralSclerosis", label: "Subchondral Sclerosis?" },
                      { key: "subchondralCysts", label: "Subchondral Cysts?" },
                      { key: "tibialSpiking", label: "Tibial Spiking?" },
                      { key: "marginalOsteophytes", label: "Marginal Osteophytes?" },
                      { key: "looseBodies", label: "Loose Bodies?" },
                      { key: "deformity", label: "Deformity?" },
                    ].map((item) => (
                      <label
                        key={item.key}
                        className="flex items-center gap-3 cursor-pointer select-none"
                      >
                        <input
                          type="checkbox"
                          checked={
                            item.key === "deformity"
                              ? data.deformity
                              : data.degenerativeFindings[item.key]
                          }
                          onChange={() =>
                            item.key === "deformity"
                              ? handleCheckboxChange(side, "deformity")
                              : setFormData((prev) => ({
                                  ...prev,
                                  [side]: {
                                    ...prev[side],
                                    degenerativeFindings: {
                                      ...prev[side].degenerativeFindings,
                                      [item.key]: !prev[side].degenerativeFindings[item.key],
                                    },
                                  },
                                }))
                          }
                          className="w-5 h-5 accent-red-600 focus:ring-red-500"
                        />
                        <span className="text-gray-800">{item.label}</span>
                      </label>
                    ))}

                    {/* Genu radio */}
                    {data.deformity && (
                      <div className="pl-6 space-y-1">
                        {["Genu Varus", "Genu Valgum"].map((g) => (
                          <label key={g} className="flex items-center gap-3">
                            <input
                              type="radio"
                              name={`${side}Genu`}
                              checked={data.genu === g}
                              onChange={() =>
                                setFormData((prev) => ({
                                  ...prev,
                                  [side]: { ...prev[side], genu: g },
                                }))
                              }
                              className="accent-red-600 focus:ring-red-500"
                            />
                            <span className="text-gray-800">{g}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Fracture */}
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={data.fracture}
                    onChange={() => handleCheckboxChange(side, "fracture")}
                    className={checkboxClass}
                  />
                  <span className="text-gray-800">Fracture?</span>
                </label>

                {/* Fracture Sub-options */}
                {data.fracture && (
                  <div className="pl-6 space-y-2">
                    {[
                      { key: "tibia", label: "Tibia?" },
                      { key: "fibula", label: "Fibula?" },
                      { key: "lowerFemur", label: "Lower Third of Femur?" },
                      { key: "patella", label: "Patella?" },
                      { key: "orthoImplants", label: "Orthopaedic Implants?" },
                    ].map((f) => (
                      <label
                        key={f.key}
                        className="flex items-center gap-3 cursor-pointer select-none"
                      >
                        <input
                          type="checkbox"
                          checked={data.fractures[f.key]}
                          onChange={() =>
                            handleCheckboxChange(side, "fractures", f.key)
                          }
                          className={checkboxClass}
                        />
                        <span className="text-gray-800">{f.label}</span>
                      </label>
                    ))}

                    {/* Bone Fracture Details */}
                    {["tibia", "fibula", "lowerFemur"].map((bone) => (
                    data.fractures[bone] && (
                        <div key={bone} className="pl-6 space-y-2">
                        <h4 className="font-medium text-gray-800 capitalize">{bone.replace(/([A-Z])/g, ' $1')} Fracture Types:</h4>
                        {[
                            { key: "LinearUndisplacedFracture", label: "Linear Undisplaced Fracture" },
                            { key: "LineardisplacedFracture", label: "Linear Displaced Fracture" },
                            { key: "ComminutedUndisplacedFracture", label: "Comminuted Undisplaced Fracture" },
                            { key: "ComminuteddisplacedFracture", label: "Comminuted Displaced Fracture" },
                            { key: "ComminutedDepressedFracture", label: "Comminuted Depressed Fracture" },
                        ].map((fracture) => (
                            <label
                            key={`${bone}-${fracture.key}`}
                            className="flex items-center gap-3 cursor-pointer select-none"
                            >
                            <input
                                type="checkbox"
                                checked={data.fractures[`${bone}FractureTypes`][fracture.key]}
                                onChange={() =>
                                setFormData((prev) => ({
                                    ...prev,
                                    [side]: {
                                    ...prev[side],
                                    fractures: {
                                        ...prev[side].fractures,
                                        [`${bone}FractureTypes`]: {
                                        ...prev[side].fractures[`${bone}FractureTypes`],
                                        [fracture.key]:
                                            !prev[side].fractures[`${bone}FractureTypes`][fracture.key],
                                        },
                                    },
                                    },
                                }))
                                }
                                className={checkboxClass}
                            />
                            <span className="text-gray-800">{fracture.label}</span>
                            </label>
                        ))}
                        </div>
                    )
                    ))}

                    {/* Ortho Implants */}
                    {data.fractures.orthoImplants && (
                    <div className="pl-6 space-y-2">
                      {[
                        { key: "orthoImplantsTibia", label: "Tibia" },
                        { key: "orthoImplantsFemur", label: "Femur" },
                        { key: "orthoImplantsFibula", label: "Fibula" },
                        { key: "orthoImplantsPatella", label: "Patella" },
                      ].map((i) => (
                        <label
                          key={i.key}
                          className="flex items-center gap-3 cursor-pointer select-none"
                        >
                          <input
                            type="checkbox"
                            checked={data.fractures[i.key]}
                            onChange={() => handleCheckboxChange(side, "fractures", i.key)}
                            className={checkboxClass}
                          />
                          <span className="text-gray-800">{i.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white overflow-y-auto p-4">
      <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b p-6">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-800">X-RAY KNEE</h1>
            <div className="flex gap-3">
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition"
                >
                  Done
                </button>
                <button className="px-6 py-2 bg-gray-800 hover:bg-gray-900 text-white rounded-lg transition">
                  Reject
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Patient Information */}
        <div className="p-6 border-b">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
            {[
              { label: "Name", field: "name", placeholder: "PRASANTA KUI" },
              { label: "Patient ID", field: "patientId", placeholder: "259B2070" },
              { label: "Age", field: "age", placeholder: "58", type: "number" },
              { label: "Test date", field: "testDate", type: "date" },
              { label: "Report date", field: "reportDate", type: "date" },
            ].map((f) => (
              <div key={f.field}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {f.label}
                </label>
                <input
                  type={f.type || "text"}
                  value={formData[f.field]}
                  onChange={(e) => handleInputChange(f.field, e.target.value)}
                  placeholder={f.placeholder}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            ))}
          </div>

          {/* Gender */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gender
            </label>
            <div className="flex gap-6">
              {["Male", "Female", "Others"].map((gender) => (
                <label key={gender} className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="radio"
                    checked={formData.gender === gender}
                    onChange={() => handleInputChange("gender", gender)}
                    className="w-5 h-5 accent-red-600 focus:ring-red-500"
                  />
                  <span className="text-gray-800">{gender}</span>
                </label>
              ))}
            </div>
          </div>

          {/* X-Ray View */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              X-Ray View*
            </label>
            <div className="flex gap-6">
              {["AP", "LATERAL", "SKYLINE", "AP/LATERAL"].map((view) => (
                <label key={view} className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="radio"
                    checked={formData.xrayView === view}
                    onChange={() => handleInputChange("xrayView", view)}
                    className="w-5 h-5 accent-red-600 focus:ring-red-500"
                  />
                  <span className="text-gray-800">{view}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Normal X-Ray */}
          <div className="mb-4">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={formData.normalXray}
                onChange={() =>
                  handleInputChange("normalXray", !formData.normalXray)
                }
                className={checkboxClass}
              />
              <span className="text-gray-800 font-medium">Normal X-Ray?</span>
            </label>
          </div>
        </div>

        {/* Left & Right Knee Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 divide-x">
          {renderKneeSection("left")}
          {renderKneeSection("right")}
        </div>
      </div>
    </div>
  );
}